// AI Turnover Scheduler — Microcopy (client v1).
//
// Single source of truth for risk labels, suggested actions and contextual
// capacity messages. Keep all customer-facing strings here so that BulkOrders,
// the Tracker and the AvailabilityEditor stay aligned.

import type {
  ConfidenceLevel,
  DayLoadFlags,
  RiskCounts,
  RiskLabel,
  RiskReasonCode,
  ServerRiskReason,
} from "./types";

export const RISK_LABEL_BADGE_CLASS: Record<RiskLabel, string> = {
  critico: "border-red-300 text-red-700",
  attenzione: "border-amber-300 text-amber-700",
  stabile: "border-emerald-300 text-emerald-700",
};

export function getSuggestedAction(
  counts: RiskCounts,
  dayFlags?: DayLoadFlags[]
): string {
  const overCapacityDays = (dayFlags ?? []).filter((d) => d.over_capacity_day).length;
  const closedWithLoadDays = (dayFlags ?? []).filter((d) => d.closed_day_with_load).length;

  if (closedWithLoadDays > 0) {
    return "Hai lavoro pianificato in giornate dichiarate chiuse. Aggiorna la disponibilita o sposta i job.";
  }
  if (overCapacityDays > 0) {
    return "Hai uno o piu giorni in over-capacity. Sposta carico, alza la capacita pubblicata o non accettare altro.";
  }
  if (counts.urgent > 0) {
    return "Conferma subito le righe in scadenza o amplia la copertura.";
  }
  if (counts.tight > 0 && counts.unscheduled > 0) {
    return "Assegna priorita ai turnover con finestra stretta e pianifica dal calendario.";
  }
  if (counts.unscheduled > 0) {
    return "Distribuisci le righe non ancora pianificate prima di accettare nuovo carico.";
  }
  return "Batch sotto controllo: monitora e aggiorna il calendario solo in caso di variazioni.";
}

/**
 * Legacy capacity microcopy used by `BulkOrdersPanel` for batch cards.
 * Kept intentionally separate from the new day-level microcopy so the panel
 * keeps its current look while the Tracker uses richer messaging.
 */
export function getBatchCapacityContext(opts: {
  closedDateCount: number;
  lowCapacityDateCount: number;
  unscheduledCount: number;
}): string | null {
  const { closedDateCount, lowCapacityDateCount, unscheduledCount } = opts;
  if (closedDateCount > 0) {
    return `Attenzione: in ${closedDateCount} ${closedDateCount === 1 ? "data utile risulta" : "date utili risultano"} fasce chiuse o senza capacita pubblicata. Se vuoi coprire questi turnover, aggiorna prima la disponibilita.`;
  }
  if (lowCapacityDateCount > 0 && unscheduledCount > 0) {
    return `Hai capacita pubblicata bassa in ${lowCapacityDateCount} ${lowCapacityDateCount === 1 ? "data toccata dal batch" : "date toccate dal batch"}. Se la tua squadra reale ha piu margine, aggiorna Availability prima di pianificare.`;
  }
  return null;
}

/** Short label used in summaries / tooltips. */
export function getCountsSummary(counts: RiskCounts): string {
  return `${counts.tight} finestre strette • ${counts.unscheduled} non pianificati • ${counts.urgent} in scadenza`;
}

// ---------------------------------------------------------------------------
// Server-side reasons & confidence (Sprint 5)
// ---------------------------------------------------------------------------

/**
 * Customer-facing label for each `risk_reasons[].code`. Stable strings — keep
 * tone operative and non-technical. Mirrors glossary section 8.1.
 */
export const RISK_REASON_LABEL: Record<RiskReasonCode, string> = {
  URGENT: "in scadenza entro 24h",
  TIGHT_WINDOW: "finestra ≤ 2 ore",
  UNSCHEDULED: "ancora senza data o orario",
  SAME_DAY_TURNOVER: "checkout e check-in nello stesso giorno",
  OVER_CAPACITY_DAY: "giorno in over-capacity",
  NEAR_CAPACITY_DAY: "giorno vicino alla saturazione",
  CLOSED_DAY_WITH_LOAD: "giorno dichiarato chiuso ma con lavoro",
  SAME_DAY_TURNOVER_CLUSTER: "cluster di turnover same-day",
};

export function getRiskReasonLabel(code: RiskReasonCode): string {
  return RISK_REASON_LABEL[code] ?? code.toLowerCase().replace(/_/g, " ");
}

/**
 * Render a server-side reason as a short pill: "{n} {label}" or just label
 * when the count is 1 and the label already implies a single item.
 */
export function formatRiskReason(reason: ServerRiskReason): string {
  const label = getRiskReasonLabel(reason.code);
  if (reason.count <= 1) return label;
  return `${reason.count} × ${label}`;
}

/**
 * Coarse confidence bucket. Boundaries chosen so that the engine v1 fallback
 * (deterministic formula) maps to "media" for the typical "1 unscheduled out
 * of 5" scenario. Anything below 0.5 is "bassa".
 */
export function getConfidenceLevel(score: number): ConfidenceLevel {
  if (!Number.isFinite(score)) return "bassa";
  const clamped = Math.max(0, Math.min(1, score));
  if (clamped >= 0.8) return "alta";
  if (clamped >= 0.5) return "media";
  return "bassa";
}

export const CONFIDENCE_BADGE_CLASS: Record<ConfidenceLevel, string> = {
  alta: "border-emerald-300 text-emerald-700",
  media: "border-amber-300 text-amber-700",
  bassa: "border-red-300 text-red-700",
};

export function getConfidenceCopy(level: ConfidenceLevel): string {
  switch (level) {
    case "alta":
      return "Affidabilita alta: lo scheduler ha dati sufficienti per il suggerimento.";
    case "media":
      return "Affidabilita media: alcune righe non hanno orario o capacita pubblicata.";
    case "bassa":
      return "Affidabilita bassa: troppi dati mancanti, completa pianificazione e disponibilita.";
  }
}

/** Human-readable message to surface inside the Tracker banner. */
export type SchedulerI18n = {
  t: (key: string) => string;
  tx: (key: string, vars: Record<string, string | number>) => string;
};

export function getCountsSummaryLocalized(counts: RiskCounts, { tx }: SchedulerI18n): string {
  return tx("scheduler.counts_summary", {
    tight: counts.tight,
    unscheduled: counts.unscheduled,
    urgent: counts.urgent,
  });
}

export function formatRiskReasonLocalized(reason: ServerRiskReason, { t, tx }: SchedulerI18n): string {
  const label = t(`scheduler.risk.${reason.code}`);
  if (reason.count <= 1) return label;
  return tx("scheduler.risk.count", { count: reason.count, label });
}

export function getSuggestedActionLocalized(
  counts: RiskCounts,
  { t }: SchedulerI18n,
  dayFlags?: DayLoadFlags[]
): string {
  const overCapacityDays = (dayFlags ?? []).filter((d) => d.over_capacity_day).length;
  const closedWithLoadDays = (dayFlags ?? []).filter((d) => d.closed_day_with_load).length;

  if (closedWithLoadDays > 0) return t("scheduler.action.closed_with_load");
  if (overCapacityDays > 0) return t("scheduler.action.over_capacity");
  if (counts.urgent > 0) return t("scheduler.action.urgent");
  if (counts.tight > 0 && counts.unscheduled > 0) return t("scheduler.action.tight_unscheduled");
  if (counts.unscheduled > 0) return t("scheduler.action.unscheduled");
  return t("scheduler.action.ok");
}

export function getBatchCapacityContextLocalized(
  opts: {
    closedDateCount: number;
    lowCapacityDateCount: number;
    unscheduledCount: number;
  },
  { t, tx }: SchedulerI18n
): string | null {
  const { closedDateCount, lowCapacityDateCount, unscheduledCount } = opts;
  if (closedDateCount > 0) {
    const dates =
      closedDateCount === 1 ? t("scheduler.batch.closed_dates_one") : t("scheduler.batch.closed_dates_many");
    return tx("scheduler.batch.closed", { count: closedDateCount, dates });
  }
  if (lowCapacityDateCount > 0 && unscheduledCount > 0) {
    const dates =
      lowCapacityDateCount === 1 ? t("scheduler.batch.low_dates_one") : t("scheduler.batch.low_dates_many");
    return tx("scheduler.batch.low_capacity", { count: lowCapacityDateCount, dates });
  }
  return null;
}

export function getConfidenceCopyLocalized(level: ConfidenceLevel, { t }: SchedulerI18n): string {
  return t(`scheduler.confidence.${level}`);
}

export function getDayFlagsBannerLocalized(
  dayFlags: Array<{ date: string } & DayLoadFlags>,
  { t, tx }: SchedulerI18n
): string | null {
  const overCapacityDays = dayFlags.filter((d) => d.over_capacity_day);
  const closedWithLoad = dayFlags.filter((d) => d.closed_day_with_load);
  const sameDayClusters = dayFlags.filter((d) => d.same_day_turnover_cluster);
  const nearCapacityDays = dayFlags.filter((d) => d.near_capacity_day);

  if (overCapacityDays.length > 0) {
    const days =
      overCapacityDays.length === 1 ? t("scheduler.banner.day_one") : t("scheduler.banner.day_many");
    return tx("scheduler.banner.over_capacity", { count: overCapacityDays.length, days });
  }
  if (closedWithLoad.length > 0) {
    const days = closedWithLoad.length === 1 ? t("scheduler.banner.date_one") : t("scheduler.banner.date_many");
    return tx("scheduler.banner.closed_with_load", { count: closedWithLoad.length, days });
  }
  if (sameDayClusters.length > 0) {
    const days =
      sameDayClusters.length === 1 ? t("scheduler.banner.day_one") : t("scheduler.banner.day_many");
    return tx("scheduler.banner.same_day_cluster", { count: sameDayClusters.length, days });
  }
  if (nearCapacityDays.length > 0) {
    const days =
      nearCapacityDays.length === 1 ? t("scheduler.banner.day_one") : t("scheduler.banner.day_many");
    return tx("scheduler.banner.near_capacity", { count: nearCapacityDays.length, days });
  }
  return null;
}

export function getDayFlagsBanner(dayFlags: Array<{ date: string } & DayLoadFlags>): string | null {
  const overCapacityDays = dayFlags.filter((d) => d.over_capacity_day);
  const closedWithLoad = dayFlags.filter((d) => d.closed_day_with_load);
  const sameDayClusters = dayFlags.filter((d) => d.same_day_turnover_cluster);
  const nearCapacityDays = dayFlags.filter((d) => d.near_capacity_day);

  if (overCapacityDays.length > 0) {
    const days = overCapacityDays.length === 1 ? "giorno" : "giorni";
    return `${overCapacityDays.length} ${days} in over-capacity rispetto alla disponibilita pubblicata. Apri il calendario per ridistribuire o aggiornare la capacita.`;
  }
  if (closedWithLoad.length > 0) {
    const days = closedWithLoad.length === 1 ? "giornata" : "giornate";
    return `Hai lavoro pianificato in ${closedWithLoad.length} ${days} dichiarate chiuse. Riapri la fascia o sposta gli interventi.`;
  }
  if (sameDayClusters.length > 0) {
    const days = sameDayClusters.length === 1 ? "giorno" : "giorni";
    return `${sameDayClusters.length} ${days} con cluster di turnover same-day: pianifica con margini ampi tra checkout e check-in.`;
  }
  if (nearCapacityDays.length > 0) {
    const days = nearCapacityDays.length === 1 ? "giorno" : "giorni";
    return `${nearCapacityDays.length} ${days} vicini alla saturazione: valuta se accettare altro carico.`;
  }
  return null;
}
